import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  Calendar, Plus, Loader2, Pencil, Trash2, Search, Users, Link, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FORMATOS_ESCALA = [
  { value: '5x2', label: '5x2 (Seg-Sex)', ciclo: 7, trabalho: 5, folga: 2 },
  { value: '6x1', label: '6x1', ciclo: 7, trabalho: 6, folga: 1 },
  { value: '6x2', label: '6x2 (6+1folga+1comp)', ciclo: 8, trabalho: 6, folga: 2 },
  { value: '5x1', label: '5x1', ciclo: 6, trabalho: 5, folga: 1 },
  { value: '12x36', label: '12x36', ciclo: 2, trabalho: 1, folga: 1 },
  { value: '4x2', label: '4x2', ciclo: 6, trabalho: 4, folga: 2 },
  { value: '3x1', label: '3x1', ciclo: 4, trabalho: 3, folga: 1 },
  { value: '4x1', label: '4x1', ciclo: 5, trabalho: 4, folga: 1 },
  { value: 'personalizado', label: 'Personalizado', ciclo: 0, trabalho: 0, folga: 0 }
];

const TIPOS_DIA = [
  { value: 'normal', label: 'Trabalho' },
  { value: 'compensado', label: 'Compensado' },
  { value: 'folga', label: 'Folga' }
];

const EscalasTrabalhoPage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [escalas, setEscalas] = useState([]);
  const [horarios, setHorarios] = useState([]);
  const [empregados, setEmpregados] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [vincularDialogOpen, setVincularDialogOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [escalaVincular, setEscalaVincular] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [erroValidacao, setErroValidacao] = useState('');
  
  const [form, setForm] = useState({
    nome: '',
    formato: '5x2',
    tamanho_ciclo: 7,
    dias_trabalho: 5,
    dias_folga: 2,
    horas_semanais: 44,
    dias: []
  });

  // Validação: dias_trabalho + dias_folga deve ser igual ao tamanho_ciclo
  const validarDias = (tamanho, trabalho, folga) => {
    const soma = (parseInt(trabalho) || 0) + (parseInt(folga) || 0);
    if (soma !== parseInt(tamanho)) {
      return `Dias Trabalho (${trabalho}) + Dias Folga/Comp. (${folga}) = ${soma} ≠ Tamanho do Ciclo (${tamanho})`;
    }
    return '';
  };

  const [vincularForm, setVincularForm] = useState({
    empregado_id: '',
    data_primeiro_folga: ''
  });

  const fetchDados = useCallback(async () => {
    try {
      const [escRes, horRes, empRes] = await Promise.all([
        fetch(`${API_URL}/api/escalas-trabalho`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/horarios`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/empregados`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      if (escRes.ok) setEscalas(await escRes.json());
      if (horRes.ok) setHorarios(await horRes.json());
      if (empRes.ok) setEmpregados(await empRes.json());
    } catch (err) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDados();
  }, [fetchDados]);

  // Gera dias do ciclo - todos como "normal" por padrão, usuário configura cada um
  const gerarDiasCiclo = (tamanhoCiclo, diasAtuais = []) => {
    const dias = [];
    
    for (let i = 1; i <= tamanhoCiclo; i++) {
      // Preserva configuração existente se houver
      const diaExistente = diasAtuais.find(d => d.dia_ciclo === i);
      if (diaExistente) {
        dias.push(diaExistente);
      } else {
        dias.push({
          dia_ciclo: i,
          horario_id: '',
          tipo_dia: 'normal',
          descricao: `Dia ${i}`
        });
      }
    }
    
    return dias;
  };

  // Quando usuário muda o tamanho do ciclo diretamente
  const handleTamanhoCicloChange = (novoTamanho) => {
    const tamanho = Math.max(2, Math.min(31, parseInt(novoTamanho) || 2));
    const dias = gerarDiasCiclo(tamanho, form.dias);
    
    // Recalcula dias de trabalho e folga baseado na configuração atual
    const diasTrabalho = dias.filter(d => d.tipo_dia === 'normal').length;
    const diasFolga = dias.filter(d => d.tipo_dia !== 'normal').length;
    
    // Valida
    const erro = validarDias(tamanho, diasTrabalho, diasFolga);
    setErroValidacao(erro);
    
    setForm({
      ...form,
      formato: 'personalizado',
      tamanho_ciclo: tamanho,
      dias_trabalho: diasTrabalho,
      dias_folga: diasFolga,
      dias
    });
  };

  // Quando usuário seleciona um formato pré-definido
  const handleFormatoChange = (formato) => {
    const config = FORMATOS_ESCALA.find(f => f.value === formato);
    if (config && formato !== 'personalizado') {
      // Gera dias baseado no formato selecionado
      const dias = [];
      for (let i = 1; i <= config.ciclo; i++) {
        const isTrabalho = i <= config.trabalho;
        dias.push({
          dia_ciclo: i,
          horario_id: '',
          tipo_dia: isTrabalho ? 'normal' : (i === config.ciclo && formato === '6x2' ? 'compensado' : 'folga'),
          descricao: isTrabalho ? `Dia ${i}` : (i === config.ciclo && formato === '6x2' ? 'Compensado' : 'Folga')
        });
      }
      setErroValidacao(''); // Limpa erro - formato pré-definido é válido
      setForm({
        ...form,
        formato,
        tamanho_ciclo: config.ciclo,
        dias_trabalho: config.trabalho,
        dias_folga: config.folga,
        dias
      });
    } else {
      // Personalizado - mantém dias atuais
      setForm({ ...form, formato });
    }
  };

  const handleDiaChange = (index, field, value) => {
    const novosDias = [...form.dias];
    novosDias[index] = { ...novosDias[index], [field]: value };
    
    // Se mudou tipo_dia para folga ou compensado, limpar horário
    if (field === 'tipo_dia' && (value === 'folga' || value === 'compensado')) {
      novosDias[index].horario_id = '';
    }
    
    // Recalcula totais
    const diasTrabalho = novosDias.filter(d => d.tipo_dia === 'normal').length;
    const diasFolga = novosDias.filter(d => d.tipo_dia !== 'normal').length;
    
    setForm({ 
      ...form, 
      dias: novosDias,
      dias_trabalho: diasTrabalho,
      dias_folga: diasFolga,
      formato: 'personalizado' // Qualquer alteração manual vira personalizado
    });
  };

  const resetForm = () => {
    const dias = gerarDiasCiclo(7);
    setForm({
      nome: '',
      formato: '5x2',
      tamanho_ciclo: 7,
      dias_trabalho: 5,
      dias_folga: 2,
      horas_semanais: 44,
      dias
    });
    setEditando(null);
  };

  const handleNovaEscala = () => {
    resetForm();
    setDialogOpen(true);
  };

  const handleEditarEscala = (escala) => {
    setForm({
      nome: escala.nome || '',
      formato: escala.formato || '5x2',
      tamanho_ciclo: escala.tamanho_ciclo || 7,
      dias_trabalho: escala.dias_trabalho || 5,
      dias_folga: escala.dias_folga || 2,
      horas_semanais: escala.horas_semanais || 44,
      dias: escala.dias || []
    });
    setEditando(escala);
    setDialogOpen(true);
  };

  const handleSalvar = async () => {
    if (!form.nome) {
      toast.error('Informe o nome da escala');
      return;
    }

    if (form.dias.length === 0) {
      toast.error('Configure os dias do ciclo');
      return;
    }

    // Validação: dias_trabalho + dias_folga = tamanho_ciclo
    const erro = validarDias(form.tamanho_ciclo, form.dias_trabalho, form.dias_folga);
    if (erro) {
      toast.error(erro);
      return;
    }

    setSaving(true);
    try {
      const url = editando 
        ? `${API_URL}/api/escalas-trabalho/${editando.id}`
        : `${API_URL}/api/escalas-trabalho`;
      
      const res = await fetch(url, {
        method: editando ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(form)
      });

      if (res.ok) {
        toast.success(editando ? 'Escala atualizada!' : 'Escala criada!');
        setDialogOpen(false);
        fetchDados();
        resetForm();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erro ao salvar');
      }
    } catch (err) {
      toast.error('Erro ao salvar escala');
    } finally {
      setSaving(false);
    }
  };

  const handleExcluir = async (escala) => {
    if (!window.confirm(`Excluir a escala "${escala.nome}"?`)) return;

    try {
      const res = await fetch(`${API_URL}/api/escalas-trabalho/${escala.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Escala excluída!');
        fetchDados();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erro ao excluir');
      }
    } catch (err) {
      toast.error('Erro ao excluir escala');
    }
  };

  const handleAbrirVincular = (escala) => {
    setEscalaVincular(escala);
    setVincularForm({ empregado_id: '', data_primeiro_folga: '' });
    setVincularDialogOpen(true);
  };

  const handleVincularEmpregado = async () => {
    if (!vincularForm.empregado_id || !vincularForm.data_primeiro_folga) {
      toast.error('Selecione o empregado e a data do primeiro dia de folga');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/escalas-trabalho/${escalaVincular.id}/vincular-empregado`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(vincularForm)
      });

      if (res.ok) {
        toast.success('Empregado vinculado à escala!');
        setVincularDialogOpen(false);
        fetchDados();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erro ao vincular');
      }
    } catch (err) {
      toast.error('Erro ao vincular empregado');
    } finally {
      setSaving(false);
    }
  };

  const escalasFiltradas = escalas.filter(e =>
    e.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.formato?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const empregadosSemEscala = empregados.filter(e => !e.escala_id || e.escala_id === '');

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Escalas de Trabalho</h1>
          <p className="text-slate-500">Configure os formatos de escala (5x2, 6x1, 12x36, etc.)</p>
        </div>
        <Button onClick={handleNovaEscala} className="gap-2" data-testid="btn-nova-escala">
          <Plus className="w-4 h-4" />
          Nova Escala
        </Button>
      </div>

      {horarios.length === 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-4">
            <p className="text-amber-800 text-sm">
              <strong>Atenção:</strong> Cadastre os horários de trabalho antes de criar as escalas.
            </p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Lista de Escalas</CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 h-9"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Formato</TableHead>
                <TableHead>Ciclo</TableHead>
                <TableHead>Trabalho/Folga</TableHead>
                <TableHead>Horas Semanais</TableHead>
                <TableHead className="w-32"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {escalasFiltradas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                    {searchTerm ? 'Nenhuma escala encontrada' : 'Nenhuma escala cadastrada'}
                  </TableCell>
                </TableRow>
              ) : (
                escalasFiltradas.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell className="font-medium">{e.nome}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{e.formato}</Badge>
                    </TableCell>
                    <TableCell>{e.tamanho_ciclo} dias</TableCell>
                    <TableCell>{e.dias_trabalho}T / {e.dias_folga}F</TableCell>
                    <TableCell>{e.horas_semanais}h</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleAbrirVincular(e)} title="Vincular empregado">
                          <Link className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleEditarEscala(e)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleExcluir(e)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog Criar/Editar Escala */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              {editando ? 'Editar Escala' : 'Nova Escala'}
            </DialogTitle>
            <DialogDescription>
              Configure o formato da escala e vincule os horários de cada dia
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nome da Escala *</Label>
                <Input
                  value={form.nome}
                  onChange={(e) => setForm({...form, nome: e.target.value})}
                  placeholder="Ex: Escala Comercial 5x2"
                />
              </div>
              <div>
                <Label>Formato *</Label>
                <Select value={form.formato} onValueChange={handleFormatoChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FORMATOS_ESCALA.map((f) => (
                      <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label>Tamanho do Ciclo *</Label>
                <Input
                  type="number"
                  min="2"
                  max="31"
                  value={form.tamanho_ciclo}
                  onChange={(e) => handleTamanhoCicloChange(e.target.value)}
                  placeholder="2 a 31 dias"
                />
                <p className="text-xs text-slate-500 mt-1">Mín: 2, Máx: 31</p>
              </div>
              <div>
                <Label>Dias Trabalho *</Label>
                <Input
                  type="number"
                  min="1"
                  max={form.tamanho_ciclo - 1}
                  value={form.dias_trabalho}
                  onChange={(e) => {
                    const valor = parseInt(e.target.value) || 0;
                    const erro = validarDias(form.tamanho_ciclo, valor, form.dias_folga);
                    setErroValidacao(erro);
                    setForm({...form, dias_trabalho: valor, formato: 'personalizado'});
                  }}
                />
              </div>
              <div>
                <Label>Dias Folga/Comp. *</Label>
                <Input
                  type="number"
                  min="1"
                  max={form.tamanho_ciclo - 1}
                  value={form.dias_folga}
                  onChange={(e) => {
                    const valor = parseInt(e.target.value) || 0;
                    const erro = validarDias(form.tamanho_ciclo, form.dias_trabalho, valor);
                    setErroValidacao(erro);
                    setForm({...form, dias_folga: valor, formato: 'personalizado'});
                  }}
                />
              </div>
              <div>
                <Label>Horas Semanais</Label>
                <Input
                  type="number"
                  value={form.horas_semanais}
                  onChange={(e) => setForm({...form, horas_semanais: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>

            {/* Mensagem de erro de validação */}
            {erroValidacao && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {erroValidacao}
              </div>
            )}

            {/* Configuração dos dias do ciclo */}
            <div>
              <Label className="mb-2 block">Dias do Ciclo</Label>
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="w-20">Dia</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Horário Vinculado</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {form.dias.map((dia, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-mono font-medium">
                          Dia {dia.dia_ciclo}
                        </TableCell>
                        <TableCell>
                          <Select 
                            value={dia.tipo_dia} 
                            onValueChange={(v) => handleDiaChange(index, 'tipo_dia', v)}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {TIPOS_DIA.map((t) => (
                                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          {dia.tipo_dia === 'normal' ? (
                            <Select 
                              value={dia.horario_id || ''} 
                              onValueChange={(v) => handleDiaChange(index, 'horario_id', v)}
                            >
                              <SelectTrigger className="w-48">
                                <SelectValue placeholder="Selecione o horário" />
                              </SelectTrigger>
                              <SelectContent>
                                {horarios.map((h) => (
                                  <SelectItem key={h.id} value={h.id}>
                                    {h.codigo} - {h.descricao}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Badge variant={dia.tipo_dia === 'compensado' ? 'outline' : 'secondary'}>
                              {dia.tipo_dia === 'compensado' ? 'Compensado' : 'Folga'}
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button onClick={handleSalvar} disabled={saving || !!erroValidacao}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editando ? 'Salvar' : 'Criar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Vincular Empregado */}
      <Dialog open={vincularDialogOpen} onOpenChange={setVincularDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Vincular Empregado à Escala
            </DialogTitle>
            <DialogDescription>
              {escalaVincular?.nome} - Defina o primeiro dia de folga para calcular o ciclo
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Empregado *</Label>
              <Select 
                value={vincularForm.empregado_id} 
                onValueChange={(v) => setVincularForm({...vincularForm, empregado_id: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o empregado" />
                </SelectTrigger>
                <SelectContent>
                  {empregadosSemEscala.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.nome} ({e.matricula || 'S/M'})
                    </SelectItem>
                  ))}
                  {empregadosSemEscala.length === 0 && (
                    <SelectItem value="" disabled>
                      Todos os empregados já têm escala
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Data do Primeiro Dia de Folga *</Label>
              <Input
                type="date"
                value={vincularForm.data_primeiro_folga}
                onChange={(e) => setVincularForm({...vincularForm, data_primeiro_folga: e.target.value})}
              />
              <p className="text-xs text-slate-500 mt-1">
                Esta data será usada como referência para calcular os dias do ciclo
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setVincularDialogOpen(false)}>Cancelar</Button>
              <Button onClick={handleVincularEmpregado} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Vincular
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EscalasTrabalhoPage;
